// Import any dependencies
import CalculateRequiredIncomeToAfford from './CalculateRequiredIncomeToAfford';
import CalculateMarketGrowthRates from './CalculateMarketGrowthRates';

// Create a function that creates an array of future home values, starting with the current value, over 10 years, with the market growth rate
export const calculate_future_home_value = (currentHomeValue, marketGrowthRate) => {
    const futureHomeValues = [];
    for (let i = 0; i <= 10; i++) {
        futureHomeValues.push(currentHomeValue * Math.pow(1 + marketGrowthRate, i));
    }
    return futureHomeValues;
}

export const calculate_projected_future_income = (currentIncome, userIncomeGrowthRate) => {
    const futureIncomes = [];
    for (let i = 0; i <= 10; i++) {
        futureIncomes.push(currentIncome * Math.pow(1 + userIncomeGrowthRate, i));
    }
    return futureIncomes;
}

// Helper function to round numbers to 3 decimal places for comparison
export const roundForComparison = (number, decimals = 3) => {
    return Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

// Helper function to prepare chart data
export const prepareChartData = (data, householdIncomeConfig) => {
    if (!data) return null;

    // Force a fresh copy of the config
    const configCopy = JSON.parse(JSON.stringify(householdIncomeConfig));

    const householdEntries = Object.entries(data)
        .filter(([key]) => key.startsWith('households_'));

    if (householdEntries.length === 0) return null;

    // Define keys for households under 75k based on config file
    const under75kKeys = [
        'households_under_10k',
        'households_10k_to_14999',
        'households_15k_to_24999',
        'households_25k_to_34999',
        'households_35k_to_49999',
        'households_50k_to_74999'
    ];

    // Separate entries into below 75k and above
    const below75k = householdEntries.filter(([key]) => under75kKeys.includes(key));
    const above75k = householdEntries.filter(([key]) => !under75kKeys.includes(key));

    // Sum up all below 75k values
    const below75kSum = below75k.reduce((sum, [_, value]) => sum + value, 0);

    // Create combined dataset
    const combinedEntries = [
        ['households_under_75k', below75kSum], // Add combined group
        ...above75k // Add all entries above 75k as is
    ];

    const fullColors = [
        "#FF4D4D",
        "#FF8C69",
        "#FFA07A",
        "#FFB366",
        "#FFD700",
        "#CCDD66",
        "#99CC66",
        "#66BB66",
        "#339966",
        "#006633"
    ]
    
    const halfColors = [
        "#FF4D4D",
        "#FFB366",
        "#FFD700",
        "#99CC66",
        "#339966",
    ]

    return {
        labels: combinedEntries.map(([key, _]) => {
            if (key === 'households_under_75k') {
                return 'Under $75k';
            }
            const config = configCopy[key];
            return config?.label || key;
        }),
        datasets: [{
            data: combinedEntries.map(([_, value]) => value),
            backgroundColor: combinedEntries.map(([key, _], index) => {
                // if (key === 'households_under_75k') {
                //     return '#FF8C69'; // Using one of the existing colors or we can pick a different one
                // }
                // return configCopy[key]?.color || '#000000';
                return halfColors[index];
            }),
            borderWidth: 0,
            hoverOffset: 4
        }]
    };
};

// Helper function to calculate CAGR - now returns decimal rounded to 4 places
export const calculateCAGR = (startValue, endValue, years = 2) => {
    const result = Math.pow(endValue / startValue, 1 / years) - 1;
    return Math.round(result * 10000) / 10000; // Rounds to 4 decimal places
};

export const formatCurrency = (value) => {
    if (value >= 1000000) {
        const millions = value / 1000000;
        // If it's a whole number or ends in .0, show no decimals
        // if (millions % 1 === 0 || millions.toFixed(1).endsWith('.0')) {
        //     return `$${Math.floor(millions)}M`;
        // }
        // Otherwise show one decimal
        return `$${millions.toFixed(2)}M`;
    }
    return `$${Math.round(value / 1000)}K`;
};

// Calculate future income required for each percentage level
export const calculateFutureIncomeRequirements = (
    percentageLevels,
    futureHomeValues,
    current_monthly_expenses,
    interestRatePercent,
    downPaymentPercent
) => {
    return percentageLevels.map(level => {
        const requiredIncomes = futureHomeValues.map(homeValue =>
            CalculateRequiredIncomeToAfford(
                homeValue,
                current_monthly_expenses,
                interestRatePercent,
                level.percentage,
                downPaymentPercent
            )
        );

        return {
            ...level,
            percentage: `${(level.percentage*100).toFixed(0)}%`,
            requiredIncome: requiredIncomes
        };
    });
}

export const analyzeIncomeGaps = (projectedFutureIncome, futureIncomeRequired, originalIncomeGaps) => {
    // Return null if projectedFutureIncome or futureIncomeRequired is null
    if (!projectedFutureIncome || !futureIncomeRequired) {
        return null;
    }

    // Initialize result object
    const result = {
        crossesAnyLevel: false,
        crossoverPoints: {},
        gapsByYear: {},
        gapTrends: {},
        overallTrend: null
    };

    // For each income requirement level (30%, 35%, etc.)
    futureIncomeRequired.forEach(level => {
        const percentage = level.percentage;
        const requiredIncome = level.requiredIncome;
        const gaps = [];
        const crossovers = [];

        // Calculate gaps for each year
        projectedFutureIncome.forEach((projectedIncome, year) => {
            const gap = requiredIncome[year] - projectedIncome;
            gaps.push(gap);

            // Check for crossover (gap changes from positive to negative or vice versa)
            if (year > 0) {
                const previousGap = gaps[year - 1];
                if ((previousGap > 0 && gap <= 0) || (previousGap <= 0 && gap > 0)) {
                    crossovers.push({
                        year,
                        fromGap: previousGap,
                        toGap: gap
                    });
                }
            }
        });

        // Calculate if gap is growing or shrinking
        const firstGap = gaps[0];
        const lastGap = gaps[gaps.length - 1];
        const gapTrend = lastGap - firstGap;

        // Calculate growth rate (as CAGR)
        const growthRate = firstGap !== 0 ?
            Math.pow(Math.abs(lastGap / firstGap), 1 / 10) - 1 :
            0;

        // Calculate total change as a percentage
        const totalChangePercent = firstGap !== 0 ?
            ((lastGap - firstGap) / Math.abs(firstGap)) :
            0;

        // Calculate gap vs income ratios
        const startGapVsIncome = Math.abs(firstGap / projectedFutureIncome[0]);
        const endGapVsIncome = Math.abs(lastGap / projectedFutureIncome[projectedFutureIncome.length - 1]);

        // Store results for this percentage level
        result.gapsByYear[percentage] = gaps.map((gap, year) => ({
            year,
            gap,
            projectedIncome: projectedFutureIncome[year],
            requiredIncome: requiredIncome[year]
        }));

        result.gapTrends[percentage] = {
            trend: gapTrend < 0 ? 'shrinking' : 'growing',
            startGap: firstGap,
            endGap: lastGap,
            totalChange: gapTrend,
            totalChangePercent: totalChangePercent,
            growthRate: growthRate,
            startGapVsIncome,
            endGapVsIncome
        };

        if (crossovers.length > 0) {
            result.crossesAnyLevel = true;
            result.crossoverPoints[percentage] = crossovers;
        }
    });

    // Calculate overall trend
    const allGapTrends = Object.values(result.gapTrends);
    const shrinkingGaps = allGapTrends.filter(trend => trend.trend === 'shrinking').length;
    const growingGaps = allGapTrends.filter(trend => trend.trend === 'growing').length;

    // Calculate risk level
    let riskLevel;
    if (result.crossesAnyLevel && shrinkingGaps > growingGaps) {
        riskLevel = 'low';
    } else if (result.crossesAnyLevel && shrinkingGaps <= growingGaps) {
        riskLevel = 'moderate';
    } else if (!result.crossesAnyLevel && shrinkingGaps > growingGaps) {
        riskLevel = 'moderate';
    } else {
        riskLevel = 'high';
    }

    // Calculate average growth rate
    const averageGrowthRate = allGapTrends.reduce((sum, trend) =>
        sum + trend.growthRate, 0
    ) / allGapTrends.length;

    // Calculate average gap vs income ratios
    const averageStartGapVsIncome = allGapTrends.reduce((sum, trend) =>
        sum + (trend.startGapVsIncome || 0), 0
    ) / allGapTrends.length;

    const averageEndGapVsIncome = allGapTrends.reduce((sum, trend) =>
        sum + (trend.endGapVsIncome || 0), 0
    ) / allGapTrends.length;

    // Get highest HI spend values (from 45% scenario)
    // Look for the highest percentage scenario (should be 45%)
    const highestHISpendScenario = Object.entries(result.gapTrends)
        .reduce((highest, [key, values]) => {
            const percentage = parseFloat(key);
            if (!isNaN(percentage) && (!highest || percentage > parseFloat(highest[0]))) {
                return [key, values];
            }
            return highest;
        }, null)?.[1] || {};

    let riskLevelScore = 0;
    if (shrinkingGaps > 0) {
        if (averageGrowthRate < -0.05) {
            riskLevelScore = 1;
        } else if (averageGrowthRate < 0) {
            riskLevelScore = 2;
        } else {
            riskLevelScore = 3;
        }
    } else if (averageEndGapVsIncome < 0.1) {
        riskLevelScore = 4;
    } else if (averageGrowthRate < 1) {
        riskLevelScore = 5;
    } else if (averageGrowthRate < 1.5) {
        riskLevelScore = 6;
    } else if (averageGrowthRate < 2) {
        riskLevelScore = 7;
    } else if (averageGrowthRate < 2.5) {
        riskLevelScore = 8;
    } else {
        riskLevelScore = 9;
    }

    let riskLevelText = "";
    if (riskLevelScore <= 3) {
        riskLevelText = "Low";
    } else if (riskLevelScore <= 6) {
        riskLevelText = "Medium";
    } else {
        riskLevelText = "High";
    }

    result.overallTrend = {
        direction: shrinkingGaps > growingGaps ? 'improving' : 'worsening',
        shrinkingLevels: shrinkingGaps,
        growingLevels: growingGaps,
        riskLevel,
        riskLevelText,
        riskLevelScore,
        averageGrowthRate,
        startGapVsIncomeHighestHISpend: highestHISpendScenario.startGapVsIncome || 0,
        endGapVsIncomeHighestHISpend: highestHISpendScenario.endGapVsIncome || 0,
        startGapVsIncomeAverageHISpend: averageStartGapVsIncome,
        endGapVsIncomeAverageHISpend: averageEndGapVsIncome
    };

    // Compare with original gaps if provided
    if (originalIncomeGaps) {
        const originalTrend = originalIncomeGaps.overallTrend;

        // Compare all numerical values
        const comparisons = {
            averageGrowthRate: {
                current: result.overallTrend.averageGrowthRate,
                original: originalTrend.averageGrowthRate,
                better: result.overallTrend.averageGrowthRate < originalTrend.averageGrowthRate
            },
            endGapVsIncomeAverageHISpend: {
                current: result.overallTrend.endGapVsIncomeAverageHISpend,
                original: originalTrend.endGapVsIncomeAverageHISpend,
                better: result.overallTrend.endGapVsIncomeAverageHISpend < originalTrend.endGapVsIncomeAverageHISpend
            },
            endGapVsIncomeHighestHISpend: {
                current: result.overallTrend.endGapVsIncomeHighestHISpend,
                original: originalTrend.endGapVsIncomeHighestHISpend,
                better: result.overallTrend.endGapVsIncomeHighestHISpend < originalTrend.endGapVsIncomeHighestHISpend
            },
            growingLevels: {
                current: result.overallTrend.growingLevels,
                original: originalTrend.growingLevels,
                better: result.overallTrend.growingLevels < originalTrend.growingLevels
            },
            shrinkingLevels: {
                current: result.overallTrend.shrinkingLevels,
                original: originalTrend.shrinkingLevels,
                better: result.overallTrend.shrinkingLevels > originalTrend.shrinkingLevels
            },
            startGapVsIncomeAverageHISpend: {
                current: result.overallTrend.startGapVsIncomeAverageHISpend,
                original: originalTrend.startGapVsIncomeAverageHISpend,
                better: result.overallTrend.startGapVsIncomeAverageHISpend < originalTrend.startGapVsIncomeAverageHISpend
            },
            startGapVsIncomeHighestHISpend: {
                current: result.overallTrend.startGapVsIncomeHighestHISpend,
                original: originalTrend.startGapVsIncomeHighestHISpend,
                better: result.overallTrend.startGapVsIncomeHighestHISpend < originalTrend.startGapVsIncomeHighestHISpend
            }
        };

        // Calculate overall rating
        const betterCount = Object.values(comparisons).filter(comp => comp.better).length;
        const totalComparisons = Object.keys(comparisons).length;

        result.vsOriginal = {
            comparisons,
            rating: betterCount > totalComparisons / 2 ? 'better' : 'worse',
            betterMetrics: betterCount,
            totalMetrics: totalComparisons
        };
    }

    return result;
};

// Grouped export
export const LTVCalculators = {
    calculate_future_home_value,
    calculate_projected_future_income,
    calculateFutureIncomeRequirements,
    analyzeIncomeGaps,
    prepareChartData,
    roundForComparison,
    calculateCAGR,
    formatCurrency
};

// Default export
export default LTVCalculators;